import React from 'react'

/**
 * useArrowScrollHandler allows you you select an items in a grid using the arrows keys
 * pressing 'left'-key will select the next item,
 * pressing 'right'-key will select previous item.
 * pressing 'down'-key will select the item in next row/column
 * pressing 'up'-key will select the item in previous row/column
 * @param indexLength the total amount of items
 * @param columns the total amount of columns in the grid
 * @param onKeyPressCallback handler that runs when selection changes, passes 'index' for the newly selected item.
 * @returns object with 'currentFocus': the current index, clearFocus: function to reset, hasRowFocus: wether any item was selected.
 */
export function useArrowScrollHandler(indexLength: number, columns = 1, onKeyPressCallback?: (index: number) => void) {
  const [currentFocus, setCurrentFocus] = React.useState(-1)
  const hasRowFocus = currentFocus !== -1

  function handleSetCurrentFocus(index: number) {
    if (index < 0 || index >= indexLength) {
      return
    }
    setCurrentFocus(index)
    onKeyPressCallback && onKeyPressCallback(index)
  }

  function clearFocus() {
    setCurrentFocus(-1)
  }

  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      if (!e.defaultPrevented) {
        // only handle this event if it wasn't handled downstream. (we don't want to interfere with autocomplete)
        const keyIndexMap: { [key: string]: number } = {
          ArrowRight: currentFocus + 1 > indexLength ? 0 : currentFocus + 1, // increment or loop back to start
          ArrowLeft: currentFocus - 1 < 0 ? indexLength - 1 : currentFocus - 1, // decrement or loop around to end
          ArrowDown: currentFocus === -1 ? 0 : currentFocus + columns, // start at first element or jump to next row
          ArrowUp: currentFocus === -1 ? indexLength : currentFocus - columns
        }
        if (keyIndexMap[e.key] !== undefined) {
          e.preventDefault()
          handleSetCurrentFocus(keyIndexMap[e.key])
        }
      }
    },
    [length, currentFocus, setCurrentFocus, columns]
  )

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, false)
    }
  }, [handleKeyDown])

  React.useEffect(() => {
    clearFocus()
  }, [indexLength])

  return { currentFocus, clearFocus, hasRowFocus }
}
